<template>
  <div>
    <template v-if="menuPicker">
      <v-menu
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset="10"
        min-width="0"
      >
        <template v-slot:activator="{ props }">
          <AppTextField
            :model-value="formatDateValue(modelValue)"
            :class="inputClass"
            :placeholder="computedInputPlaceholder"
            :clearable="clearable"
            readonly
            v-bind="{...props, ...$attrs }"
            @clear="onClear"
          >
          <template v-for="(_, slot) in $slots" v-slot:[slot]>
            <slot :name="slot" />
          </template>
          </AppTextField>
        </template>
        <v-date-picker
          v-model="computedValue"
          :color="pickerColor"
          hide-actions
          :min="computedMin"
          v-bind="$attrs"
          @update:model-value="menu = false"
        />
      </v-menu>
    </template>
    <template v-else>
      <v-date-picker
        v-model="computedValue"
        :color="pickerColor"
        :hide-actions="true"
        :min="computedMin"
        v-bind="$attrs"
      />
    </template>
  </div>
</template>

<script>
import dayjs from 'dayjs'

import AppTextField from './AppTextField.vue'
import { ISOToShortenedDate, formatDateValue } from './utils/dates'
/**
 * Default Date picker
 * @displayName AppDatePicker
 */
/**
   * A wrapper around the date picker component with default styling and custom default behavior
   * It is invoked on a menu through a text field by default and proxies props to it when configured with the menuPicker
   * All Vuetify props for the date picker component are usable.
   * @see See doc : https://vuetifyjs.com/en/api/v-date-picker/#props
   */
export default {
  name: 'AppDatePicker',
  components: {
    AppTextField,
  },
  inheritAttrs: false,
  emits: [
    'hidden',
    'show',
    'update:model-value',
  ],
  props: {
    /**
     * @model
     * Value of Date picker
     * @values String or [String]
     */
    modelValue: {
      required: true,
    },
    /**
     * If the picker is invoked on a menu through a text field. Defaults to true
     * @default true
     */
    menuPicker: {
      type: Boolean,
      default: true,
    },
    /**
     * A class given to the internal input of AppDatePicker component.
     * We can't pass a class through normal means, so we put a named prop for it
     * @default ''
     */
    inputClass: {
      default: '',
    },
    /**
     * Color variant of picker
     * @default primary
     */
    pickerColor: {
      type: String,
      default: 'primary',
    },
    /**
     * Display the clear icon in the date field
     * @default true
     */
    clearable: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      menu: false,
    }
  },
  computed: {
    computedInputPlaceholder () {
      if (this.usesRangePicker && !this.$attrs.placeholder) {
        return ISOToShortenedDate(dayjs().subtract(1, 'months').format('YYYY-MM-DD')) + ' - ' + ISOToShortenedDate(dayjs().format('YYYY-MM-DD'))
      } else {
        return this.$attrs.placeholder
      }
    },
    usesRangePicker () {
      return this.$attrs.multiple === '' || this.$attrs.multiple
    },
    computedMin () {
      return this.$attrs.min || (this.usesRangePicker && this.modelValue[0])
    },
    computedValue: {
      get () {
        return this.modelValue ? new Date(this.modelValue) : undefined
      },
      set (newValue) {
        /**
         * Input event.
         * @property {string, array} value
         */
        this.$emit('update:model-value', newValue)
      },
    },
  },
  watch: {
    menu (newValue) {
      this.$emit(newValue ? 'show' : 'hidden')
    },
  },
  created () {
    if (this.usesRangePicker) {
      console.warn(`DEPRECATED: Don't use the range picker option here. Use AppDateRangePicker instead.\n
      This will be removed entirely in a later commit`)
    }
  },
  methods: {
    formatDateValue,
    onClear () {
      if (this.usesRangePicker) {
        this.$emit('update:model-value', [])
      } else {
        this.$emit('update:model-value', '')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.v-input__control *) {
  cursor: pointer;
}
</style>
<!--<docs>-->
<!--```vue-->
<!--<template>-->
<!--  <AppDatePicker v-model="date"-->
<!--                 range-->
<!--  />-->
<!--</template>-->
<!--<script>-->
<!--  export default {-->
<!--    data () {-->
<!--      return {-->
<!--        date: []-->
<!--      }-->
<!--    },-->
<!--  }-->
<!--</script>-->
<!--```-->
<!--</docs>-->
